import { AllVerificationsProgress } from '../utils';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { UserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { RequestState } from 'shared/auth/hooks/useRequestState';

export type FeedbackType =
  | 'Full (TI + Donor)'
  | 'TI Only'
  | 'Donor Only'
  | 'No Feedback';

export interface SubmitProps {
  total: number;
  reportId: string | number;
  userType: UserType;
  isLoading?: boolean;
  errorMessage?: string;
  feedbackType: FeedbackType;
  isThirdPartyReport: boolean;
  feedbackStatus: FeedbackStatus;
  requestUpdateReportState: RequestState<unknown>;
  handleBack: () => unknown;
  onDone: (status: FeedbackStatus) => unknown;
  className?: string;
}

export const isAllVerified = (
  progress: AllVerificationsProgress,
  total: number,
): boolean => {
  const keys = Object.keys(progress);
  let count = 0;
  for (const k of keys) {
    count++;
    const p = progress[k];
    if (!p.donorVerified && !p.tiVerified) {
      return false;
    }
  }
  // there might be previously resolved feedback
  return count >= total;
};

export interface Report {
  dbId: number;
  endDate: string;
  errors: number;
  feedbackType: FeedbackType;
  fiscalYearEnd: string | null;
  fiscalYearStart: string | null;
  name: string;
  portfolioName: string;
  sponsorName: string | null;
  startDate: string | null;
  tasks: string;
  totalDonations: number;
  warnings: number;
  year: string; // "2019"
}

const fullFeedbackCycle = (
  status: FeedbackStatus,
  userType: UserType,
  reassignNPO: boolean,
): FeedbackStatus => {
  switch (status) {
    case 'Draft':
      return 'Awaiting TI Feedback';

    case 'Awaiting TI Feedback':
      return 'Awaiting Funder Feedback';

    case 'Awaiting Funder Feedback':
      return 'Funder Feedback Provided';

    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      return 'Awaiting TI Verification';

    case 'Awaiting TI Verification':
    case 'Awaiting Funder Verification':
      if (reassignNPO) return 'TI Feedback Provided';
      return 'Published';

    case 'Published':
    case 'Finalized':
    case 'Interim Updates Needed':
    case 'Final Updates Needed':
    default:
      return 'Published';
  }
};

const donorOnlyFeedbackCycle = (
  status: FeedbackStatus,
  userType: UserType,
  reassignNPO: boolean,
): FeedbackStatus => {
  switch (status) {
    case 'Draft':
      return 'Awaiting Funder Feedback';

    case 'Awaiting TI Feedback':
    case 'Awaiting Funder Feedback':
      return 'Funder Feedback Provided';

    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      return 'Awaiting Funder Verification';

    case 'Awaiting TI Verification':
    case 'Awaiting Funder Verification':
      if (reassignNPO) return 'Funder Feedback Provided';
      return 'Published';

    case 'Published':
    case 'Finalized':
    case 'Interim Updates Needed':
    case 'Final Updates Needed':
    default:
      return 'Published';
  }
};

const tiOnlyFeedbackCycle = (
  status: FeedbackStatus,
  userType: UserType,
  reassignNPO: boolean,
): FeedbackStatus => {
  switch (status) {
    case 'Draft':
      return 'Awaiting TI Feedback';

    case 'Awaiting TI Feedback':
    case 'Awaiting Funder Feedback':
      return 'TI Feedback Provided';

    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      return 'Awaiting TI Verification';

    case 'Awaiting TI Verification':
    case 'Awaiting Funder Verification':
      if (reassignNPO) return 'TI Feedback Provided';
      return 'Published';

    case 'Published':
    case 'Finalized':
    case 'Interim Updates Needed':
    case 'Final Updates Needed':
    default:
      return 'Published';
  }
};

export const isNoFeedbackGiven = (
  type: FeedbackType,
  status: FeedbackStatus,
  userType: UserType,
  total: number,
): boolean => {
  return (
    total === 0 &&
    (status === 'Awaiting TI Feedback' || status === 'Awaiting Funder Feedback')
  );
};

export const getNextFeedbackStatus = (
  type: FeedbackType,
  status: FeedbackStatus,
  userType: UserType,
  reassignNPO: boolean,
  total: number,
): FeedbackStatus => {
  const noFeedbackGiven = isNoFeedbackGiven(type, status, userType, total);
  if (noFeedbackGiven) return 'Published';
  switch (type) {
    case 'Full (TI + Donor)':
      return fullFeedbackCycle(status, userType, reassignNPO);

    case 'Donor Only':
      return donorOnlyFeedbackCycle(status, userType, reassignNPO);

    case 'TI Only':
      return tiOnlyFeedbackCycle(status, userType, reassignNPO);

    case 'No Feedback':
    default:
      return 'Published';
  }
};
